<template>
  <Loader v-if="loading" />
  <div v-else-if="torneo">
    <!-- Header Section -->
    <Card class="mb-4">
      <template #title>
        <div class="flex justify-content-between align-items-center">
          <div>
            <h2 class="m-0">{{ torneo.nome }}</h2>
            <small class="text-secondary">{{ torneo.luogo }} | {{ formatDate(torneo.data_inizio) }} - {{ formatDate(torneo.data_fine) }}</small>
          </div>
          <div class="flex gap-2">
            <Button icon="pi pi-list" label="Stampa Distinta" class="p-button-info" @click="showDistinta" />
            <Button icon="pi pi-arrow-left" label="Torna ai Tornei" class="p-button-secondary" @click="$router.push('/tornei')" />
          </div>
        </div>
      </template>
      <template #content>
        <div class="grid">
          <div class="col-12 md:col-3">
            <div class="p-3 border-round surface-100">
              <span class="block text-secondary font-bold mb-2">CAMPIONATO</span>
              <span class="text-xl">{{ torneo.campionato || 'N/D' }}</span>
            </div>
          </div>
          <div class="col-12 md:col-3">
            <div class="p-3 border-round surface-100">
              <span class="block text-secondary font-bold mb-2">COSTO</span>
              <span class="text-xl">€ {{ torneo.costo }}</span>
            </div>
          </div>
          <div class="col-12 md:col-6">
            <div class="p-3 border-round surface-100">
              <span class="block text-secondary font-bold mb-2"> NOTE </span>
              <span>{{ torneo.descrizione || 'Nessuna nota aggiuntiva.' }}</span>
            </div>
          </div>
        </div>
      </template>
    </Card>

    <!-- Stats Summary -->
    <div class="grid mb-4">
      <div class="col-12 md:col-3">
        <div class="card p-3 shadow-1 flex align-items-center justify-content-between surface-card">
          <div>
            <span class="block text-500 font-medium mb-3">Convocabili</span>
            <div class="text-900 font-medium text-xl">{{ torneo.people.length }}</div>
          </div>
          <div class="flex align-items-center justify-content-center bg-blue-100 border-round" style="width:2.5rem;height:2.5rem">
            <i class="pi pi-users text-blue-500 text-xl"></i>
          </div>
        </div>
      </div>
      <div class="col-12 md:col-3">
        <div class="card p-3 shadow-1 flex align-items-center justify-content-between surface-card">
          <div>
            <span class="block text-500 font-medium mb-3">Convocati</span>
            <div class="text-900 font-medium text-xl">{{ convocatiCount }}</div>
          </div>
          <div class="flex align-items-center justify-content-center bg-green-100 border-round" style="width:2.5rem;height:2.5rem">
            <i class="pi pi-check-circle text-green-500 text-xl"></i>
          </div>
        </div>
      </div>
      <div class="col-12 md:col-3">
        <div class="card p-3 shadow-1 flex align-items-center justify-content-between surface-card">
          <div>
            <span class="block text-500 font-medium mb-3">Disponibili</span>
            <div class="text-900 font-medium text-xl">{{ disponibiliCount }}</div>
          </div>
          <div class="flex align-items-center justify-content-center bg-orange-100 border-round" style="width:2.5rem;height:2.5rem">
            <i class="pi pi-thumbs-up text-orange-500 text-xl"></i>
          </div>
        </div>
      </div>
      <div class="col-12 md:col-3">
        <div class="card p-3 shadow-1 flex align-items-center justify-content-between surface-card">
          <div>
            <span class="block text-500 font-medium mb-3">Entrate</span>
            <div class="text-900 font-medium text-xl">€ {{ totalRevenue }} / {{ expectedRevenue }}</div>
          </div>
          <div class="flex align-items-center justify-content-center bg-purple-100 border-round" style="width:2.5rem;height:2.5rem">
            <i class="pi pi-wallet text-purple-500 text-xl"></i>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content Tabs -->
    <TabView >
      <TabPanel header="Convocazioni / Iscritti">
        <div class="flex flex-column gap-3 mb-3" style="padding: 20px;">
          <div class="flex justify-content-between align-items-center">
            <h3 class="m-0">Gestione Atleti</h3>
            <div class="flex gap-2">
              <Button label="Salva Convocazioni" icon="pi pi-save" @click="saveConvocazioni" class="p-button-success" />
            </div>
          </div>
          <div class="flex align-items-center gap-2 bg-blue-50 p-3 border-round border-blue-200" style="border: 1px solid">
            <span class="font-bold text-blue-800">Aggiungi manualmente atleta da un altro gruppo:</span>
            <AutoComplete
              v-model="atletaSelezionato"
              :suggestions="atletiFiltrati"
              field="nomecompletoUtente"
              placeholder="Cerca per nome e cognome..."
              forceSelection
              :minLength="0"
              @complete="filterAtleti"
              class="w-20rem"
            />
            <Button label="Aggiungi" icon="pi pi-plus" @click="addAtletaManuale" class="p-button-primary p-button-sm" :disabled="!atletaSelezionato" />
          </div>
        </div>
        <DataTable :value="torneo.people" class="p-datatable-gridlines p-datatable-sm" responsiveLayout="scroll">
          <Column field="atleta" header="Atleta" sortable>
            <template #body="{data}">
              <span class="font-bold cursor-pointer text-primary" @click="$router.push(`/atleta/${data.id_atleta}/dettagli`)">
                {{ capitalize(data.atleta) }}
              </span>
            </template>
          </Column>
          <Column field="datadinascita" header="Nato il" sortable>
            <template #body="{data}">{{ formatShortDate(data.datadinascita) }}</template>
          </Column>
          <Column field="presenze_ultimi_due_mesi" header="Pres. (2m)" sortable style="text-align: center"></Column>
          <Column field="convocazioni_ultimi_due_mesi" header="Conv. (2m)" sortable style="text-align: center"></Column>
          <Column field="confermato" header="Disponibile" sortable>
            <template #body="{data}">
              <Tag :value="formatStatus(data.confermato)" :severity="statusSeverity(data.confermato)" />
            </template>
          </Column>
          <Column field="numero_maglia" header="N. Maglia">
             <template #body="{data}">
                <InputText v-model="data.numero_maglia" type="number" style="width: 60px" @blur="saveNumeroMaglia(data)" />
             </template>
          </Column>
          <Column header="Convoca" style="text-align: center; width: 80px">
            <template #body="{data}">
              <Checkbox v-model="selectedConvocati" :value="data.id" />
            </template>
          </Column>
        </DataTable>
      </TabPanel>

      <TabPanel header="Pagamenti">
        <div class="flex justify-content-between align-items-center mb-3" style="padding: 20px;">
          <h3 class="m-0">Stato Pagamenti</h3>
          <div class="flex gap-2">
            <Button label="Genera Link Stripe" icon="pi pi-link" class="p-button-warning" @click="generatePaymentLinks" :disabled="!torneo.costo" />
            <Button label="Invia Email" icon="pi pi-envelope" class="p-button-info" @click="sendEmails" />
            <Button label="Invia Promemoria" icon="pi pi-bell" class="p-button-help" @click="sendPaymentReminder" />
          </div>
        </div>
        <DataTable :value="convocatiOnly" class="p-datatable-gridlines p-datatable-sm" responsiveLayout="scroll">
          <Column field="atleta" header="Atleta" sortable></Column>
          <Column field="importo" header="Da Pagare">
            <template #body="{data}">€ {{ data.importo || 0 }}</template>
          </Column>
          <Column field="importo_pagato" header="Pagato">
            <template #body="{data}">
              <span :class="{'text-green-500 font-bold': data.importo_pagato >= data.importo, 'text-red-500': data.importo_pagato < data.importo}">
                € {{ data.importo_pagato || 0 }}
              </span>
            </template>
          </Column>
          <Column header="Link Stripe">
             <template #body="{data}">
                <div v-if="data.stripe_payment_link_url" class="flex gap-2 align-items-center">
                   <InputText :value="data.stripe_payment_link_url" readonly class="text-xs p-1 flex-1" />
                   <Button icon="pi pi-copy" class="p-button-text p-button-sm" @click="copyLink(data.stripe_payment_link_url, data.atleta)" />
                </div>
                <span v-else class="text-secondary text-xs">Non generato</span>
             </template>
          </Column>
          <Column header="Azioni" style="width: 150px">
            <template #body="{data}">
              <Button v-if="!data.importo" label="Agg. Debito" class="p-button-text p-button-sm" @click="aggiungiDaPagare(data)" />
            </template>
          </Column>
        </DataTable>
      </TabPanel>

      <TabPanel header="Rimborsi Staff">
        <div class="flex justify-content-between align-items-center mb-3" style="padding: 20px;">
          <h3 class="m-0">Rimborsi Mister / Collaboratori</h3>
        </div>
        <DataTable :value="staffReimbursements" class="p-datatable-gridlines p-datatable-sm mb-4" responsiveLayout="scroll">
          <Column field="nome" header="Staff"></Column>
          <Column field="amount" header="Importo">
            <template #body="{data}">€ {{ parseFloat(data.amount).toFixed(2) }}</template>
          </Column>
          <Column style="width: 80px">
            <template #body="{data}">
              <Button icon="pi pi-trash" class="p-button-danger p-button-text" @click="deleteReimbursement(data.id)" />
            </template>
          </Column>
        </DataTable>

        <div class="surface-ground p-3 border-round">
          <h4 class="mt-0">Aggiungi Rimborso</h4>
          <div class="grid">
            <div class="col-12 md:col-5">
              <label class="block mb-2">Staff</label>
              <Dropdown v-model="newReimbursement.staff" :options="allStaff" optionLabel="nome" placeholder="Seleziona..." class="w-full" filter />
            </div>
            <div class="col-12 md:col-4">
              <label class="block mb-2">Importo (€)</label>
              <InputNumber v-model="newReimbursement.amount" mode="currency" currency="EUR" locale="it-IT" class="w-full" />
            </div>
            <div class="col-12 md:col-3 flex align-items-end">
              <Button label="Aggiungi" icon="pi pi-plus" class="w-full" @click="addReimbursement" :disabled="!newReimbursement.staff || !newReimbursement.amount" />
            </div>
          </div>
        </div>
      </TabPanel>
    </TabView>
  </div>
  <div v-else class="p-5 text-center">
    <h3>Torneo non trovato</h3>
    <Button label="Torna indietro" @click="$router.push('/tornei')" />
  </div>
</template>

<script>
import { ref, onMounted, computed, getCurrentInstance } from 'vue'
import { useRoute } from 'vue-router'
import { useToast } from 'primevue/usetoast'
import Loader from "@/components/Loader.vue"
import Service from '@/services/levante-api/levante-api'
import moment from 'moment'
import servicesConfig from '@/config/services.js'
import AutoComplete from 'primevue/autocomplete'

export default {
  name: 'TorneoDetail',
  components: { Loader, AutoComplete },
  setup() {
    const route = useRoute()
    const toast = useToast()
    const self = getCurrentInstance()
    const service = new Service(self.appContext.config.globalProperties.$eventBus)

    const loading = ref(true)
    const torneo = ref(null)
    const selectedConvocati = ref([])
    const staffReimbursements = ref([])
    const allStaff = ref([])
    const newReimbursement = ref({ staff: null, amount: 0 })

    const isStripe = ref(true) // Default to true based on other components, or could be from process.env

    const allAtleti = ref([])
    const atletiFiltrati = ref([])
    const atletaSelezionato = ref(null)

    const loadAllAtleti = async () => {
      try {
        const res = await service.getAtleti()
        if (res.items) {
          allAtleti.value = res.items.map(a => ({
            ...a,
            nomecompletoUtente: `${a.cognome} ${a.nome}`
          }))
        }
      } catch(e) {
        console.error(e)
      }
    }

    const loadData = async () => {
      try {
        loading.value = true
        const id = route.params.id
        const res = await service.getDetailTorneo(id)
        torneo.value = res.items
        
        // Initialize selected convocati
        selectedConvocati.value = torneo.value.people
          .filter(p => p.convocato == 1)
          .map(p => p.id)

        // Load reimbursements
        const reimRes = await service.getConvocationReimbursements(id)
        if (reimRes.items && reimRes.items.status) {
          staffReimbursements.value = reimRes.items.data || []
        }

        // Load all staff for selection
        const staffRes = await service.getAllStaff()
        if (staffRes.items && staffRes.items.status) {
          allStaff.value = staffRes.items.data || []
        }
      } catch (e) {
        console.error(e)
        toast.add({ severity: 'error', summary: 'Errore', detail: 'Errore nel caricamento del torneo' })
      } finally {
        loading.value = false
      }
    }

    const init = async () => {
      await loadData();
      await loadAllAtleti();
    }
    
    onMounted(init)

    // Computeds
    const convocatiCount = computed(() => selectedConvocati.value.length)
    const disponibiliCount = computed(() => torneo.value?.people.filter(p => p.confermato == 1).length || 0)
    const convocatiOnly = computed(() => torneo.value?.people.filter(p => p.convocato == 1) || [])
    
    const expectedRevenue = computed(() => {
      if (!torneo.value) return 0
      return convocatiOnly.value.length * torneo.value.costo
    })
    
    const totalRevenue = computed(() => {
      if (!torneo.value) return 0
      return convocatiOnly.value.reduce((acc, p) => acc + (p.importo_pagato || 0), 0)
    })

    // Methods
    const formatCurrency = (val) => {
      return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(val || 0);
    }
    const formatDate = (d) => d ? moment(d).format('DD/MM/YYYY HH:mm') : ''
    const formatShortDate = (d) => d ? moment(d).format('DD/MM/YYYY') : ''
    
    const capitalize = (str) => {
      if (!str) return ''
      return str.toLowerCase().split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
    }

    const filterAtleti = (event) => {
      const q = (event.query || '').toLowerCase()
      atletiFiltrati.value = allAtleti.value.filter(a =>
        a.nomecompletoUtente.toLowerCase().includes(q)
      )
    }

    const addAtletaManuale = async () => {
      if (!atletaSelezionato.value) return;
      const fd = new FormData();
      fd.append('id_torneo', route.params.id);
      fd.append('id_atleta', atletaSelezionato.value.id);
      try {
        const res = await service.addAtletaManualeTorneo(fd);
        if (res.item && res.item.esito === 'success') {
          toast.add({ severity: 'success', summary: 'Aggiunto', detail: 'Atleta aggiunto al torneo', life: 3000 });
          atletaSelezionato.value = null;
          loadData();
        } else {
          toast.add({ severity: 'error', summary: 'Errore', detail: res.item ? res.item.messaggio : 'Impossibile aggiungere atleta' });
        }
      } catch (e) {
        toast.add({ severity: 'error', summary: 'Errore', detail: 'Impossibile aggiungere atleta' });
      }
    }

    const formatStatus = (s) => {
      if (s == 1) return 'SI'
      if (s == 2) return 'NO'
      return 'IN ATTESA'
    }

    const statusSeverity = (s) => {
      if (s == 1) return 'success'
      if (s == 2) return 'danger'
      return 'warning'
    }

    const showDistinta = () => {
      window.open(`${servicesConfig.levante_api.url}/stampa-distinta-torneo/${route.params.id}`, '_blank')
    }

    const saveNumeroMaglia = async (person) => {
      const fd = new FormData()
      fd.append("id_torneo", route.params.id)
      fd.append("id_atleta", person.id_atleta)
      fd.append("numero_maglia", person.numero_maglia || "")
      
      const res = await service.saveNumeroMaglia(fd)
      if (res.item.esito === 'success') {
        toast.add({ severity: 'success', summary: 'Salvato', detail: 'Numero maglia aggiornato', life: 2000 })
      }
    }

    const saveConvocazioni = async () => {
      const fd = new FormData()
      const data = {
        id: torneo.value.id,
        nome: torneo.value.nome,
        luogo: torneo.value.luogo,
        costo: torneo.value.costo,
        stato: torneo.value.pubblicato,
        note: torneo.value.descrizione,
        convocati: selectedConvocati.value
      }
      fd.append('torneo', JSON.stringify(data))
      
      const res = await service.saveConvocazione(fd)
      if (res.item.esito === 'success') {
        toast.add({ severity: 'success', summary: 'Successo', detail: 'Convocazioni salvate', life: 3000 })
        loadData()
      }
    }

    const generatePaymentLinks = async () => {
      const res = await service.generatePaymentLink(route.params.id, torneo.value.costo)
      if (res.item.esito === 'success') {
        toast.add({ severity: 'success', summary: 'Link Generati', detail: res.item.messaggio })
        loadData()
      }
    }

    const sendEmails = async () => {
      const res = await service.sendConvocationEmails(route.params.id)
      if (res.item.esito === 'success') {
        toast.add({ severity: 'success', summary: 'Email Inviate', detail: res.item.messaggio })
      }
    }

    const sendPaymentReminder = async () => {
      const res = await service.sendPaymentReminderEmails(route.params.id)
      if (res.item.esito === 'success') {
        toast.add({ severity: 'success', summary: 'Promemoria Inviati', detail: res.item.messaggio })
      }
    }

    const copyLink = (link, name) => {
      navigator.clipboard.writeText(link)
      toast.add({ severity: 'info', summary: 'Copiato', detail: `Link di ${name} copiato negli appunti`, life: 2000 })
    }

    const aggiungiDaPagare = async (person) => {
      const fd = new FormData()
      fd.append("userId", person.id_atleta)
      fd.append("torneo", route.params.id)
      fd.append("costo", torneo.value.costo)
      
      const res = await service.aggiungiDaPagareTorneo(fd)
      if (res.item.esito === 'success') {
        toast.add({ severity: 'success', summary: 'Successo', detail: 'Debito aggiunto' })
        loadData()
      }
    }

    const deleteReimbursement = async (id) => {
      await service.deleteConvocationReimbursement(id)
      staffReimbursements.value = staffReimbursements.value.filter(r => r.id !== id)
      toast.add({ severity: 'success', summary: 'Eliminato', detail: 'Rimborso rimosso' })
    }

    const addReimbursement = async () => {
      const reimbursements = [{
        mister_id: newReimbursement.value.staff.id,
        amount: newReimbursement.value.amount
      }]
      
      await service.saveConvocationReimbursements(route.params.id, reimbursements)
      newReimbursement.value = { staff: null, amount: 0 }
      loadData()
      toast.add({ severity: 'success', summary: 'Aggiunto', detail: 'Rimborso registrato' })
    }

    return {
      loading, torneo, selectedConvocati, staffReimbursements, allStaff, newReimbursement, isStripe,
      convocatiCount, disponibiliCount, convocatiOnly, expectedRevenue, totalRevenue,
      formatDate, formatShortDate, capitalize, formatStatus, statusSeverity, formatCurrency,
      showDistinta, saveNumeroMaglia, saveConvocazioni, generatePaymentLinks,
      sendEmails, sendPaymentReminder, copyLink, aggiungiDaPagare,
      deleteReimbursement, addReimbursement, filterAtleti, addAtletaManuale, allAtleti, atletiFiltrati, atletaSelezionato
    }
  }
}
</script>

<style scoped>
.card {
  background: var(--surface-card);
  border: 1px solid var(--surface-border);
  border-radius: 12px;
  transition: transform 0.2s, box-shadow 0.2s;
}

.card:hover {
  box-shadow: 0 4px 20px 0 rgba(0,0,0,0.1);
}

.surface-100 {
  background-color: var(--surface-100) !important;
}

:deep(.p-tabview-panels) {
  padding: 1.5rem 0;
}

:deep(.p-datatable.p-datatable-sm .p-datatable-tbody > tr > td) {
  padding: 0.5rem 0.5rem;
}
</style>
